import { useEffect, useState } from "react"
import ReactQuill from "react-quill"
import { toast } from "react-toastify"
import clsx from "clsx"

import { Modal } from "components/modals/Modal"

import { gameService } from "services/game/game.service"

import "react-quill/dist/quill.snow.css"

interface GameDescriptionModalProps {
  open: boolean
  game: {
    identifier: string
    title: string
    description?: string | null
  } | null
  onClose: () => void
  onSaved: (savedDescription: string | null) => void
}

const QUILL_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ["bold", "italic", "underline", "strike"],
    [{ list: "ordered" }, { list: "bullet" }],
    [{ align: [] }],
    ["link"],
    ["clean"]
  ]
}

const QUILL_FORMATS = ["header", "bold", "italic", "underline", "strike", "list", "bullet", "align", "link"]

export const GameDescriptionModal = ({ open, game, onClose, onSaved }: GameDescriptionModalProps) => {
  const [html, setHtml] = useState("")
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (open && game) {
      setHtml(game.description ?? "")
      setSaving(false)
    }
  }, [open, game?.identifier])

  if (!game) return null

  const isEffectivelyEmpty = (value: string): boolean => {
    const stripped = value.replace(/<p>\s*<br\s*\/?>\s*<\/p>/gi, "").trim()
    return stripped.length === 0
  }

  const handleSave = async () => {
    if (saving) return
    setSaving(true)
    const toSend = isEffectivelyEmpty(html) ? null : html
    const saved = await gameService.updateGameDescription(game.identifier, toSend)
    setSaving(false)
    if (saved === undefined) return
    toast.success(saved === null ? "Description cleared" : "Description saved")
    onSaved(saved ?? null)
  }

  const handleClear = async () => {
    if (saving) return
    setSaving(true)
    const saved = await gameService.updateGameDescription(game.identifier, null)
    setSaving(false)
    if (saved === undefined) return
    setHtml("")
    toast.success("Description cleared")
    onSaved(null)
  }

  const hasContent = !isEffectivelyEmpty(html)

  return (
    <Modal open={open} handler={onClose}>
      <div className="modal-box w-[min(720px,95vw)] text-[rgba(6,7,21,1)] dark:text-white">
        <header className="flex flex-col gap-1 mb-4">
          <h3 className="font-bold text-lg">Game description</h3>
          <p className="text-sm opacity-70 break-words">
            {game.title} · <span className="font-mono">{game.identifier}</span>
          </p>
          <p className="text-xs opacity-60">
            Supports headings, bold/italic/underline/strike, lists, alignment, and links. Anything outside that allowlist will be stripped
            on save.
          </p>
        </header>

        <div className="bg-white rounded-md overflow-hidden text-black border border-black/10">
          <ReactQuill
            theme="snow"
            value={html}
            onChange={setHtml}
            modules={QUILL_MODULES}
            formats={QUILL_FORMATS}
            style={{ minHeight: 240 }}
          />
        </div>

        <div className="modal-action gap-2">
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button
            type="button"
            className={clsx("btn btn-outline", "border-[rgba(132,_0,_168,_0.5)] text-[rgba(132,_0,_168,_1)] dark:text-white")}
            onClick={handleClear}
            disabled={saving || (!hasContent && (game.description ?? "").length === 0)}
            title="Remove the description entirely"
          >
            Clear
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className={clsx(
              "btn btn-sm bg-[rgba(132,_0,_168,_1)] hover:bg-[rgba(169,_78,_206,_1)] text-white border-transparent disabled:opacity-40"
            )}
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </Modal>
  )
}
